import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AxiosRequestConfig, AxiosError } from 'axios';
import axios from 'axios';
import type { AuthRole } from '@/types';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${API_BASE}/api`;

/**
 * Shared axios instance — preserves the same interceptor behavior
 * (token attach, 401 refresh) that existed in services/api.ts.
 */
export const apiClient = axios.create({
	baseURL: API,
	headers: { 'Content-Type': 'application/json' },
});

// Attach auth token
apiClient.interceptors.request.use((config) => {
	const token = localStorage.getItem('alforaa_access_token');
	if (token) {
		config.headers.Authorization = `Bearer ${token}`;
	}
	return config;
});

// Singleton refresh promise — prevents concurrent requests from each triggering
// their own refresh call. Multiple 401s will all await the same in-flight refresh
// and reuse the new access token, avoiding token-theft-detection false positives.
let refreshPromise: Promise<string> | null = null;

// 401 → token refresh
apiClient.interceptors.response.use(
	(res) => res,
	async (error) => {
		const originalRequest = error.config;
		if (error.response?.status === 401 && !originalRequest._retry) {
			originalRequest._retry = true;
			const refreshToken = localStorage.getItem('alforaa_refresh_token');
			const role = localStorage.getItem('alforaa_role') as AuthRole | null;
			if (refreshToken && role) {
				try {
					if (!refreshPromise) {
						const refreshMap: Record<AuthRole, string> = {
							student: '/auth/student/refresh',
							parent: '/auth/parent/refresh',
							trainer: '/auth/trainer/refresh',
							school_admin: '/auth/school-admin/refresh',
							master_admin: '/auth/master-admin/refresh',
							salesperson: '/auth/salesperson/refresh',
							sales_manager: '/auth/sales-manager/refresh',
							appointment_coordinator:
								'/auth/appointment-coordinator/refresh',
							zonal_manager: '/auth/zonal-manager/refresh',
						};
						refreshPromise = axios
							.post(`${API}${refreshMap[role]}`, { refreshToken })
							.then((res) => {
								localStorage.setItem(
									'alforaa_access_token',
									res.data.accessToken,
								);
								localStorage.setItem(
									'alforaa_refresh_token',
									res.data.refreshToken,
								);
								return res.data.accessToken as string;
							})
							.finally(() => {
								refreshPromise = null;
							});
					}
					const newAccessToken = await refreshPromise;
					originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
					return apiClient(originalRequest);
				} catch (refreshErr: any) {
					// Refresh genuinely failed (token expired / revoked).
					// Log the user out so they can re-authenticate.
					const studentIdPrefix = localStorage.getItem('studentIdPrefix');
					const loginPath = localStorage.getItem('alforaa_login_path') || '/';
					localStorage.clear();
					if (studentIdPrefix)
						localStorage.setItem('studentIdPrefix', studentIdPrefix);
					window.location.href = loginPath;
				}
			}
		}
		return Promise.reject(error);
	},
);

/**
 * RTK Query base query that wraps our existing axios instance.
 * This preserves all interceptor behavior (token attach, 401 refresh).
 */
export interface AxiosBaseQueryArgs {
	url: string;
	method?: AxiosRequestConfig['method'];
	data?: AxiosRequestConfig['data'];
	params?: AxiosRequestConfig['params'];
	headers?: AxiosRequestConfig['headers'];
}

export const axiosBaseQuery: BaseQueryFn<
	AxiosBaseQueryArgs,
	unknown,
	{ status?: number; data?: unknown; message?: string }
> = async ({ url, method = 'GET', data, params, headers }) => {
	try {
		const result = await apiClient({ url, method, data, params, headers });
		return { data: result.data };
	} catch (err) {
		const axiosError = err as AxiosError;
		return {
			error: {
				status: axiosError.response?.status,
				data: axiosError.response?.data,
				message: axiosError.message,
			},
		};
	}
};
